import { Box, Typography } from "@mui/material";
import { MRT_ColumnDef } from "material-react-table";
import { accountRatingOptions, accountStatusOptions, capitalizeFirstLetter, displayValue, formatCurrency, NumberFilterValue, TimelineTooltipPayload, relationshipTypeFilterOptions, flightRiskOptions, ACCOUNT_THEME_TAGS_CONFIG } from "../../utils/utils";
import TimelineHeader from "./TimelineHeader";
import ColumnFilter from "../Common/customFilter";
import DescSortIcon from "../../assets/common/Desc-sort-icon.svg";
import AscSortIcon from "../../assets/common/Asc-sort-icon.svg";
import CustomerTimelineCell from "./customerTimeLineCell";
import QuickTopicsPopper from "../Common/QuickTopicsPopper";
import { getTrajectoryStyles } from "../ClientOverview/FingertipMetricView.styles";
import "../../styles/account.css";
import flagRiskIcon from "../../assets/accounts/account_flight_risk_flag_icon.svg";
import TagGroup, { TagItem } from "../Common/TagGroup";
import { ThemeCategoryKey } from "../../interfaces/themes";
import EllipsedTooltip from "../Common/EllipsedTooltip";

const initialSort = { sort_field: "account", sort_order: "asc" as "asc" | "desc" };

const getCustomerAccountColumns = (
  dateRange: { startDate: string; endDate: string },
  columnFilters: any,
  setColumnFilters: React.Dispatch<React.SetStateAction<any>>,
  sort: { sort_field: string; sort_order?: "asc" | "desc" },
  onSortChange: (field: string, order: "asc" | "desc") => void,
  timelineTooltip: {
    anchorEl: HTMLElement | null;
    activeItem: TimelineTooltipPayload | null;
    setAnchorEl: (el: HTMLElement | null) => void;
    setActiveItem: (item: TimelineTooltipPayload | null) => void;
  },
  openFilterColumn: string | null,
  setOpenFilterColumn: React.Dispatch<React.SetStateAction<string | null>>,
  columnVisibility: Record<string, boolean>,
  clientTypeOptions: { label: string; value: string }[],
  trajectoryOptions: { label: string; value: string }[],
  isSpacesView?: boolean,
  onAccountSummaryTagClick?: (row: any, themeCategoryKey: ThemeCategoryKey) => void,
  isSummaryActive?: boolean,
  isFlightRiskCreateUpdateEnabled?: boolean,
): MRT_ColumnDef<any>[] => {
  const isAccountFilterActive = 
    openFilterColumn === "account" ||
    (Array.isArray(columnFilters.account) &&
      columnFilters.account.length > 0);

  const isMrrFilterActive =
    openFilterColumn === "mrr" || !!columnFilters.mrr;

  const isRatingFilterActive =
    openFilterColumn === "rating" ||
    (Array.isArray(columnFilters.rating) && columnFilters.rating.length > 0) ||
    (typeof columnFilters.rating === "string" && columnFilters.rating !== "");
  const isCustomerTypeFilterActive = openFilterColumn === "customerType" ||
    (Array.isArray(columnFilters.customerType) &&
      columnFilters.customerType.length > 0);

  const isAccountManagerFilterActive = openFilterColumn === "accountManager" ||
    (Array.isArray(columnFilters.accountManager) &&
      columnFilters.accountManager.length > 0);
  const isStatusFilterActive = openFilterColumn === "status" ||
    (Array.isArray(columnFilters.status) &&
      columnFilters.status.length > 0);

  const isTrajectoryFilterActive =
    openFilterColumn === "trajectory" || !!columnFilters.trajectory;
  
  const isAdvertisingAccountManagerFilterActive = openFilterColumn === "advertisingAccountManagerName" ||
    (Array.isArray(columnFilters.advertisingAccountManagerName) &&
      columnFilters.advertisingAccountManagerName.length > 0);

  const handleHeaderFilterClick = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    timelineTooltip?.setAnchorEl(null);
    timelineTooltip?.setActiveItem(null);
    const target = e.target as HTMLElement;

    if (
      target.closest(".cf-sort-click") ||
      target.closest(".cf-icon-button") ||
      target.closest(".cf-popper")
    ) {
      return;
    }

    const filterBtn = e.currentTarget.querySelector(
      ".cf-icon-button"
    ) as HTMLElement | null;

    filterBtn?.click();
  };

  const isRelationshipTypeFilterActive =
    openFilterColumn === "accountRelationshipType" ||
    !!columnFilters.accountRelationshipType;

  const isFlightRiskFilterActive =
    openFilterColumn === "flightRisk" ||
    columnFilters.flightRisk !== undefined;
  return [
    {
    accessorKey: "account",
    header: "",
    Header: (
            <Box
              onClick={handleHeaderFilterClick}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "6px",
                backgroundColor: openFilterColumn === "account" ? "#F2F2F2" : "inherit",
              }}>
            <Typography
                className="cf-title" 
                sx={{ color: Array.isArray(columnFilters?.account) &&
                            columnFilters.account.length > 0 ? "#6036d6" : "inherit"}}
              >
            {isSpacesView ? "Account Name" : "Customer Name"} </Typography>
              {sort?.sort_field === "account" && (
                <Box
                  className="cf-sort-click"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newOrder =
                      sort.sort_order === "asc" ? "desc" : "asc";
                    onSortChange?.("account", newOrder);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    marginLeft: "4px",
                  }}
                >
                  {sort.sort_order === "asc" ? (
                    <img src={AscSortIcon} className="cf-sort-icon-active" />
                  ) : (
                    <img src={DescSortIcon} className="cf-sort-icon-active" />
                  )}
                </Box>
              )}
            <ColumnFilter
              currentValue={columnFilters.account ?? []}
              onApply={(val) =>
                setColumnFilters((prev: any) => ({
                  ...prev,
                  account:
                    typeof val === "string" && val.trim()
                      ? [val.trim()]
                      : undefined,
                }))
              }
              options={[]}
              type="multi"
              showSections={{
                apply: false,
                options: false,
                sort: true,
              }}
              columnKey="account" 
              placeHolder={"Search by Account"}
              onOpen={() => setOpenFilterColumn("account")}
              onClose={() => setOpenFilterColumn(null)}
              isActive={isAccountFilterActive}
              sortDirection={
                sort?.sort_field === "account" ? sort.sort_order : null
              }
              onSortChange={onSortChange}
              enableSearchMode={true}
            />
          </Box>
      ) as unknown as string,
    size: 175,
    enableColumnFilter: false,
    enableSorting: false,
    grow: isSummaryActive ? false : undefined,
    minSize: 150,
    muiTableHeadCellProps: {
      sx:{
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          padding: "5px",
      }
    },
    Cell: ({row }: { row: any }) => {
        const formattedName = capitalizeFirstLetter(row.original?.name);
        const clientDomain = row.original?.domain;
        const orgId = row.original?.orgId;
        const subOrgId = row.original?.subOrgId;
        const handleClientClick = () => {
          sessionStorage.setItem("selectedClient", clientDomain);
          row.original?.onClientClick(
            clientDomain,
            row.original?.name,
            true,
            orgId,
            subOrgId,
            row.original?.accountRelationshipType,
            row.original?.salesforceAccountUrl,
            row.original?.salesforceAccountId
          );
        };
        return (
        <Box
            sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            minWidth: 0,
            padding: "4px 12px",
            gap: "6px",
            }}
        >
            {/* Account Name */}
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                flexGrow: 1,
                flexShrink: 1,
                minWidth: 0,
                overflow: "hidden",
              }}
            >
              <EllipsedTooltip tooltipContent={displayValue(formattedName)} maxWidth="100%">
                <span
                  style={{
                    textDecoration: "underline",
                    textDecorationColor: "rgba(25, 118, 210, 0.4)",
                    color: "#1976d2",
                    cursor: "pointer",
                  }}
                  onClick={handleClientClick}
                >
                  {displayValue(formattedName)}
                </span>
              </EllipsedTooltip>
              {row.original?.rating === "Detractor" && (
                <Box sx={{ flexShrink: 0, marginLeft: "4px", display: "flex", alignItems: "center" }}>
                  <QuickTopicsPopper
                    orgId={row.original?.orgId}
                    subOrgId={row.original?.subOrgId}
                  />
                </Box>
              )}
            </Box>
        </Box>
        );
    },
    },
    {
      accessorKey: "clientType",
      header: "",
      Header: (
        <Box
          onClick={handleHeaderFilterClick}
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "6px",
            backgroundColor: openFilterColumn === "clientType" ? "#F2F2F2" : "inherit",
          }}>
          <Typography
            className="cf-title" 
            sx={{ color: Array.isArray(columnFilters?.clientType) &&
                        columnFilters.clientType.length > 0 ? "#6036d6" : "inherit"}}
          >
          Customer Type </Typography>
          {sort?.sort_field === "clientType" && (
            <Box
              className="cf-sort-click"
              onClick={(e) => {
                e.stopPropagation();
                const newOrder =
                  sort.sort_order === "asc" ? "desc" : "asc";
                onSortChange?.("clientType", newOrder);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                cursor: "pointer",
                marginLeft: "4px",
              }}
            >
              {sort.sort_order === "asc" ? (
                <img src={AscSortIcon} className="cf-sort-icon-active" />
              ) : (
                <img src={DescSortIcon} className="cf-sort-icon-active" />
              )}
            </Box>
          )}
          <ColumnFilter
            currentValue={columnFilters.clientType ?? []}
            onApply={(val) =>
              setColumnFilters((prev: any) => ({
                ...prev,
                clientType:
                  Array.isArray(val) && val.length > 0
                    ? val
                    : undefined,
              }))
            }
            options={clientTypeOptions}
            type="multi"
            showSections={{
              apply: true,
              options: true,
              sort: true,
              search: false,
            }}
            columnKey="clientType" 
            placeHolder={"Search..."}
            onOpen={() => setOpenFilterColumn("clientType")}
            onClose={() => setOpenFilterColumn(null)}
            isActive={isCustomerTypeFilterActive}
            sortDirection={
              sort?.sort_field === "clientType" ? sort.sort_order : null
            }
            onSortChange={onSortChange}
          />
        </Box>
      ) as unknown as string,
    size: 155,
    grow: isSummaryActive ? false : undefined,
    enableColumnFilter: false,
    enableSorting: false,
    minSize: 140,
    muiTableHeadCellProps: {
      sx:{
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          padding: "5px",
      }
    },
    Cell: ({row }: { row: any }) => {
        const formattedName = capitalizeFirstLetter(row.original?.clientType);
        return (
        <Box
            sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            padding: "4px 12px",
            }}
        >
            {/* Account Name */}
            <Box
            sx={{
              flexGrow: 0,
              flexShrink: 1,
              flexBasis: "auto",
              cursor: "pointer",
            }}
            >
            {displayValue(formattedName)}
            </Box>
        </Box>
        );
    },
    },
    {
      accessorKey: "accountRelationshipType",
      header: "",
      Header: (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "6px",
            backgroundColor:
              openFilterColumn === "accountRelationshipType" ? "#F2F2F2" : "inherit",
          }}
        >
          <Typography
            className="cf-title"
            sx={{
              color: columnFilters?.accountRelationshipType
                ? "#6036d6"
                : "inherit",
            }}
          >
            Type
          </Typography>

          <ColumnFilter
            currentValue={columnFilters?.accountRelationshipType ?? ""}
            onApply={(val) =>
              setColumnFilters((prev: any) => ({
                ...prev,
                accountRelationshipType:
                  typeof val === "string" && val.trim()
                    ? val.trim()
                    : undefined,
              }))
            }
            options={relationshipTypeFilterOptions}
            type="single"
            showSections={{
              apply: true,
              options: true,
              sort: false,
              search: false,
            }}
            columnKey="accountRelationshipType"
            onOpen={() => setOpenFilterColumn("accountRelationshipType")}
            onClose={() => setOpenFilterColumn(null)}
            isActive={isRelationshipTypeFilterActive}
            parent_page="accounts_page"
          />
        </Box>
      ) as unknown as string,

      size: 130,
      enableColumnFilter: false,
      enableSorting: false,
      minSize: 150,

      muiTableHeadCellProps: {
        sx: {
          justifyContent: "center",
          alignItems: "center",
          borderBottom: "1px solid #7A7A8C",
          "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight: "1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          padding: "10px",
        },
      },

      Cell: ({ row }: { row: any }) => (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            padding: "4px 12px",
            color: "#43434D",
            fontSize: "13px",
          }}
        >
          {capitalizeFirstLetter(row.original?.accountRelationshipType ?? "") || "No Type Selected"}
        </Box>
      ),
    },
    {
      accessorKey: "status",
      header: "",
      Header: (
        <Box
          onClick={handleHeaderFilterClick}
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "6px",
            backgroundColor: openFilterColumn === "status" ? "#F2F2F2" : "inherit",
          }}>
          <Typography
            className="cf-title" 
            sx={{ color: Array.isArray(columnFilters?.status) &&
                        columnFilters.status.length > 0 ? "#6036d6" : "inherit"}}
          >
          Status </Typography>
          <ColumnFilter
            currentValue={columnFilters.status?.[0] ?? ""}
            onApply={(val) =>
              setColumnFilters((prev: any) => ({
                ...prev,
                status:
                  typeof val === "string" && val.trim()
                    ? [val.trim()]
                    : undefined,
              }))
            }
            options={accountStatusOptions.map((o: any) => ({
              label: o.label,
              value: String(o.value),
            }))}
            type="single"
            showSections={{
              apply: true,
              options: true,
              sort: false,
              search: false,
            }}
            columnKey="status" 
            onOpen={() => setOpenFilterColumn("status")}
            onClose={() => setOpenFilterColumn(null)}
            isActive={isStatusFilterActive}
            parent_page="accounts_page"
          />
        </Box>
      ) as unknown as string,
    size: 90,
    grow: isSummaryActive ? false : undefined,
    enableColumnFilter: false,
    enableSorting: false,
    minSize: 90,
    muiTableHeadCellProps: {
      sx:{
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          padding: "5px",
      }
    },
    Cell: ({ row }: { row: any }) => {
      const status = row.original?.status;
      return (
        <Box
          sx={{
            padding: "4px 12px",
            fontSize: "12px",
          }}
        >
          {status ? "Active" : "In-Active"}
        </Box>
      );
    },
    },
    {
      accessorKey: "mrr",
      header: "",
      Header: (
            <Box
              onClick={handleHeaderFilterClick}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "6px",
                backgroundColor: openFilterColumn === "mrr" ? "#F2F2F2" : "inherit",
              }}>
            <Typography
                className="cf-title" 
                sx={{
                  color:
                    columnFilters?.mrr
                      ? "#6036d6"
                      : "inherit",
                      paddingRight: "3px"
                }}
              >
            MRR </Typography>
            {sort?.sort_field === "mrr" && (
              <Box
                className="cf-sort-click"
                onClick={(e) => {
                  e.stopPropagation();
                  const newOrder =
                    sort.sort_order === "asc" ? "desc" : "asc";
                  onSortChange?.("mrr", newOrder);
                }}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                }}
              >
                {sort.sort_order === "asc" ? (
                  <img src={AscSortIcon} className="cf-sort-icon-active" />
                ) : (
                  <img src={DescSortIcon} className="cf-sort-icon-active" />
                )}
              </Box>
            )}
            <ColumnFilter
              currentValue={columnFilters.mrr}
              onApply={(val) =>
                setColumnFilters((prev: any) => ({
                  ...prev,
                  mrr: val as NumberFilterValue | undefined,
                }))
              }
              options={[]}
              type="number"
              showSections={{
                sort: true,
                search: false,
              }}
              columnKey="mrr" 
              onOpen={() => setOpenFilterColumn("mrr")}
              onClose={() => setOpenFilterColumn(null)}
              isActive={isMrrFilterActive}
              sortDirection={
                sort?.sort_field === "mrr" ? sort.sort_order : null
              }
              onSortChange={onSortChange}
            />
          </Box>
      ) as unknown as string,
      size: 80,
      grow: isSummaryActive ? false : true,
      minSize: 80,
      enableColumnFilter: false,
      enableSorting: false,
      muiTableBodyCellProps: {
        sx: {
          justifyContent: "left",
          alignItems: "center",
          "&[data-pinned='true']:before": {
              boxShadow: "none !important",
              backgroundColor: "#FFFFFF !important",
              borderRight:"1px solid #C2C2CA",
            },
            "&[data-pinned='true']:hover": {
              backgroundColor: "#F2F2F4 !important",
              cursor: "pointer",
            },
        },
      },
      muiTableHeadCellProps: {
        sx:{
          justifyContent: "center",
          alignItems: "center",
          borderBottom: "1px solid #7A7A8C",
          "&[data-pinned='true']:before": {
              boxShadow: "none !important",
              backgroundColor: "#FFFFFF !important",
              borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
           padding: "5px",
        }
      },
      Cell: ({ row }) => {
        const raw = row.original.Mrr;
        const mrr: number = typeof raw === "number" ? raw : Number(raw ?? 0);
        return (
          <Box
            sx={{
                fontSize: "12px",
                color: "#43434D",
                lineHeight: "18px",
            }}
            >
              {raw !== null && raw !== undefined
                ? formatCurrency(mrr)
                : "---"}
          </Box>
        );
      },
    },
  {
    accessorKey: "rating",
    header: "",
    Header: (
            <Box
              onClick={handleHeaderFilterClick}
              sx={{
                display: "flex",
                alignItems: "center",
                width: "auto",
                padding: "6px",
                backgroundColor: openFilterColumn === "rating" ? "#F2F2F2" : "inherit",
              }}>
            <Typography
                className="cf-title" 
                sx={{
                  color:
                    (Array.isArray(columnFilters?.rating) && columnFilters.rating.length > 0) ||
                    (typeof columnFilters?.rating === "string" && columnFilters.rating !== "")
                      ? "#6036d6"
                      : "inherit",
                }}
              >
            Current Rating </Typography>
            <ColumnFilter
              currentValue={ columnFilters.rating || []}
              onApply={(val) => setColumnFilters((prev: any) => ({ ...prev, rating: val }))}
              options={accountRatingOptions}
              type="multi"
              showSections={{
                apply: true,
                sort: false,
                search: false,
              }}
              columnKey="rating" 
              onOpen={() => setOpenFilterColumn("rating")}
              onClose={() => setOpenFilterColumn(null)}
              isActive={isRatingFilterActive}
            />
          </Box>
      ) as unknown as string,
    size: 145,
    grow: isSummaryActive ? false : undefined,
    enableColumnFilter: false,
    enableSorting: false,
    muiTableBodyCellProps: {
      sx: {
        justifyContent: "center",
        alignItems: "center",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
      },
    },
    muiTableHeadCellProps: {
      sx:{
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          padding: "5px",
      }
    },
    Cell: ({ cell }) => {
      const rating = cell.getValue();

      if (!rating) return <Box>---</Box>;

      if (
        typeof rating === "string" &&
        (rating.toLowerCase() === "not scorable" ||
          rating.toLowerCase() === "too early to score" ||
          rating.toLowerCase() === "not rated")
      ) {
        return (
          <Box sx={{ fontSize: "12px", color: "#43434D" }}>
            {rating}
          </Box>
        );
      }

      const color =
        rating === "Promoter"
          ? "#04BD00"
          : rating === "Detractor"
          ? "#F4442F"
          : rating === "Neutral" 
          ? "#FFCD2A"
          : "#EFEBFB";

      return (
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "4px",
            bgcolor: color,
          }}
        />
      );
    },
  },
  {
    accessorKey: "trajectory",
    header: "",
    Header: (
      <Box 
        onClick={handleHeaderFilterClick}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "6px",
          backgroundColor: openFilterColumn === "trajectory" ? "#F2F2F2" : "inherit",
        }}>
        <Typography
          className="cf-title" 
          sx={{
            color:
              columnFilters?.trajectory
                ? "#6036d6"
                : "inherit",
          }}
        >
        Trajectory </Typography>
        <ColumnFilter
          currentValue={ columnFilters.trajectory || ""}
          onApply={(val) => setColumnFilters((prev: any) => ({
            ...prev,
            trajectory: Array.isArray(val) ? val[0] : val,
          }))}
          options={trajectoryOptions}
          type="single"
          showSections={{
            sort: false,
            search: false,
          }}
          columnKey="trajectory" 
          onOpen={() => setOpenFilterColumn("trajectory")}
          onClose={() => setOpenFilterColumn(null)}
          isActive={isTrajectoryFilterActive}
        />
      </Box>
    ),
    size: 115,
    grow: isSummaryActive ? false : undefined,
    enableColumnFilter: false,
    enableSorting: false,
    muiTableBodyCellProps: {
      sx: {
        justifyContent: "center",
        alignItems: "center",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
      },
    },
    muiTableHeadCellProps: {
      sx:{
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          padding: "5px",
      }
    },
    Cell: ({ row }) => {
      const trajectory = row.original?.relationshipTrajectory;
      const relationshipTrajectoryFlag =
        row.original?.relationshipTrajectoryFlag;

      if (!trajectory) return <Box>---</Box>;

      const trajectoryStyles = getTrajectoryStyles(
        relationshipTrajectoryFlag
      );

      return (
        <Box
          sx={{
            ...trajectoryStyles,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "4px 4px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: 500,
            width: "fit-content",
          }}
        >
          {trajectory}
        </Box>
      );
    },
  },
  {
    accessorKey: "accountManagerName",
    header: "",
    Header: (
            <Box
              onClick={handleHeaderFilterClick}
              sx={{
                display: "flex",
                alignItems: "center",
                padding: "6px",
                backgroundColor: openFilterColumn === "accountManagerName" ? "#F2F2F2" : "inherit",
              }}>
            <Typography
                className="cf-title" 
                sx={{ color: Array.isArray(columnFilters?.accountManagerName) &&
                            columnFilters.accountManagerName.length > 0 ? "#6036d6" : "inherit"}}
              >
            Account Manager </Typography>
              {sort?.sort_field === "accountManagerName" && (
                <Box
                  className="cf-sort-click"
                  onClick={(e) => {
                    e.stopPropagation();
                    const newOrder =
                      sort.sort_order === "asc" ? "desc" : "asc";
                    onSortChange?.("accountManagerName", newOrder);
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "pointer",
                    marginLeft: "4px",
                  }}
                >
                  {sort.sort_order === "asc" ? (
                    <img src={AscSortIcon} className="cf-sort-icon-active" />
                  ) : (
                    <img src={DescSortIcon} className="cf-sort-icon-active" />
                  )}
                </Box>
              )}
            <ColumnFilter
              currentValue={columnFilters.accountManagerName ?? []}
              onApply={(val) =>
                setColumnFilters((prev: any) => ({
                  ...prev,
                  accountManagerName:
                    typeof val === "string" && val.trim()
                      ? [val.trim()]
                      : undefined,
                }))
              }
              options={[]}
              type="multi"
              showSections={{
                apply: false,
                options: false,
                sort: true,
              }}
              columnKey="accountManagerName" 
              placeHolder={"Search..."}
              onOpen={() => setOpenFilterColumn("accountManagerName")}
              onClose={() => setOpenFilterColumn(null)}
              isActive={isAccountManagerFilterActive}
              sortDirection={
                sort?.sort_field === "accountManagerName" ? sort.sort_order : null
              }
              onSortChange={onSortChange}
              enableSearchMode={true}
            />
          </Box>
      ) as unknown as string,
    size: 170,
    grow: isSummaryActive ? false : undefined,
    enableColumnFilter: false,
    enableSorting: false,
    minSize: 170,
    muiTableHeadCellProps: {
      sx:{
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
            boxShadow: "none !important",
            backgroundColor: "#FFFFFF !important",
            borderRight:"1px solid #C2C2CA",
          },
          "&[data-pinned='true']:hover": {
            backgroundColor: "#F2F2F4 !important",
            cursor: "pointer",
          },
          padding: "5px",
      }
    },
    Cell: ({row }: { row: any }) => {
        const formattedName = capitalizeFirstLetter(row.original?.accountManagerName);
        return (
        <Box
            sx={{
            display: "flex",
            flexDirection: "column",
            gap: "2px",
            justifyContent: "flex-start",
            alignItems: "flex-start",
            padding: "4px 12px",
            }}
        >
            {/* Account Name */}
            <Box
            sx={{
              flexGrow: 0,
              flexShrink: 1,
              flexBasis: "auto",
              cursor: "pointer",
            }}
            >
            {displayValue(formattedName)}
            </Box>
        </Box>
        );
    },
  },
  {
    accessorKey: "flightRisk",
    header: "",
    Header: (
      <Box
        onClick={handleHeaderFilterClick}
        className={`flight-risk-header-box${
          openFilterColumn === "flightRisk" ? " flight-risk-header-box--active" : ""
        }`}
      >
        <Typography
          className={`cf-title flight-risk-header-title${
            columnFilters?.flightRisk !== undefined
              ? " flight-risk-header-title--active"
              : ""
          }`}
        >
          Flight Risk
        </Typography>
        <ColumnFilter
          currentValue={
            columnFilters.flightRisk !== undefined
              ? String(columnFilters.flightRisk)
              : ""
          }
          onApply={(val) =>
            setColumnFilters((prev: any) => ({
              ...prev,
              flightRisk:
                val === "true"
                  ? true
                  : val === "false"
                  ? false
                  : typeof val === "boolean"
                  ? val
                  : undefined,
            }))
          }
          options={flightRiskOptions.map((o) => ({
            label: o.label,
            value: String(o.value),
          }))}
          type="single"
          showSections={{
            apply: true,
            options: true,
            sort: false,
            search: false,
          }}
          columnKey="flightRisk"
          onOpen={() => setOpenFilterColumn("flightRisk")}
          onClose={() => setOpenFilterColumn(null)}
          isActive={isFlightRiskFilterActive}
          parent_page="accounts_page"
        />
      </Box>
    ) as unknown as string,
    size: 115,
    minSize: 115,
    grow: isSummaryActive ? false : undefined,
    enableColumnFilter: false,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: {
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
          boxShadow: "none !important",
          backgroundColor: "#FFFFFF !important",
          borderRight: "1px solid #C2C2CA",
        },
        "&[data-pinned='true']:hover": {
          backgroundColor: "#F2F2F4 !important",
          cursor: "pointer",
        },
        padding: "5px",
      },
    },
    Cell: ({ row }: { row: any }) => {
      const hasFlightRisk = Boolean(row.original?.flightRisk);
      const orgId = row.original?.orgId;
      const subOrgId = row.original?.subOrgId;
      const accountName = row.original?.name;
      const accountManagerName = row.original?.accountManagerName || "";

      const handleClick = () => {
        row?.original?.onFlightRiskClick?.(orgId, subOrgId, accountName, false, accountManagerName);
      };

      const handleCreateClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        row?.original?.onFlightRiskClick?.(orgId, subOrgId, accountName, true, accountManagerName);
      };

      if (!hasFlightRisk) {
        if (!isFlightRiskCreateUpdateEnabled) {
          return (
            <div className="flight-risk-cell-container">
              <span className="flight-risk-fallback-dash">---</span>
            </div>
          );
        }
        return (
          <div
            className="flight-risk-cell-container flight-risk-cell-container--create"
            onClick={handleCreateClick}
            title="Create Flight Risk"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleCreateClick(e as any); }}
          >
            <div className="flight-risk-create-btn">
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flight-risk-create-icon"
              >
                <circle cx="8" cy="8" r="6.75" stroke="currentColor" strokeWidth="1.5" />
                <path d="M8 5V11M5 8H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
              <span className="flight-risk-create-text">Flight Risk</span>
            </div>
          </div>
        );
      }

      return (
        <div className="flight-risk-cell-container" onClick={handleClick}>
          <div className="flight-risk-badge">
            <img src={flagRiskIcon} alt="Flight Risk" />
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "advertisingAccountManagerName",
    header: "Advertising Account Manager",
    Header: (
      <Box
        onClick={handleHeaderFilterClick}
        sx={{
          display: "flex",
          alignItems: "center",
          padding: "6px",
          backgroundColor: openFilterColumn === "advertisingAccountManagerName" ? "#F2F2F2" : "inherit",
        }}>
      <Typography
          className="cf-title" 
          sx={{ color: Array.isArray(columnFilters?.advertisingAccountManagerName) &&
                      columnFilters.advertisingAccountManagerName.length > 0 ? "#6036d6" : "inherit"}}
        >
          Advertising Account Manager
        </Typography>
        {sort?.sort_field === "advertisingAccountManagerName" && (
          <Box
            className="cf-sort-click"
            onClick={(e) => {
              e.stopPropagation();
              const newOrder =
                sort.sort_order === "asc" ? "desc" : "asc";
              onSortChange?.("advertisingAccountManagerName", newOrder);
            }}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {sort.sort_order === "asc" ? (
              <img src={AscSortIcon} className="cf-sort-icon-active" />
            ) : (
              <img src={DescSortIcon} className="cf-sort-icon-active" />
            )}
          </Box>
        )}
        <ColumnFilter
          currentValue={columnFilters.advertisingAccountManagerName ?? []}
          onApply={(val) => {
            const nextValue = Array.isArray(val) ? val[0] : val;
            setColumnFilters((prev: any) => ({
              ...prev,
              advertisingAccountManagerName:
                typeof nextValue === "string" && nextValue.trim()
                  ? [nextValue.trim()]
                  : undefined,
            }));
          }}
          options={[]}
          type="multi"
          showSections={{
            apply: false,
            options: false,
            sort: true,
            search: true,
          }}
          columnKey="advertisingAccountManagerName"
          placeHolder={"Search..."}
          onOpen={() => setOpenFilterColumn("advertisingAccountManagerName")}
          onClose={() => setOpenFilterColumn(null)}
          isActive={isAdvertisingAccountManagerFilterActive}
          sortDirection={
            sort?.sort_field === "advertisingAccountManagerName" ? sort.sort_order : null
          }
          onSortChange={onSortChange}
          enableSearchMode={true}
        />
      </Box>
    ) as unknown as string,
    minSize: 280,
    size: 280,
    grow: isSummaryActive ? false : undefined,
    enableColumnFilter: false,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: {
        justifyContent: "center",
        alignItems: "center",
        borderBottom: "1px solid #7A7A8C",
        "&[data-pinned='true']:before": {
          boxShadow: "none !important",
          backgroundColor: "#FFFFFF !important",
          borderRight: "1px solid #C2C2CA",
        },
        "&[data-pinned='true']:hover": {
          backgroundColor: "#F2F2F4 !important",
          cursor: "pointer",
        },
        padding: "5px",
      },
    },
    Cell: ({ row }: { row: any }) => {
      const rawName = row.original?.advertisingAccountManagerName;
      const name = Array.isArray(rawName)
        ? rawName.filter(Boolean).join(", ")
        : rawName;

      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            padding: "4px 12px",
          }}
        >
          {displayValue(typeof name === "string" ? capitalizeFirstLetter(name) : name)}
        </Box>
      );
    },
  },
  {
    accessorKey: "accountSummary",
    header: "",
    Header: (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Typography className="cf-title flight-risk-header-title">
          Account Summary
        </Typography>
      </Box>
    ) as unknown as string,
    minSize: 600,
    size: 600,
    grow: true,
    enableColumnFilter: false,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: {
        borderBottom: "1px solid #7A7A8C",
        padding: "10px",
      },
    },
    Cell: ({ row }: { row: any }) => {
      const accountSummary = row.original?.accountSummary;
      
      if (!accountSummary) {
        return (
          <div className="account-summary-empty">
            ---
          </div>
        );
      }

      const summaryText = accountSummary.fingertipExecutiveSummary || "---";
      const themes = accountSummary.themes || {};

      const tagItems: TagItem[] = ACCOUNT_THEME_TAGS_CONFIG.map((config) => {
        const count = Number(themes[config.key] ?? 0);
        const isDisabled = count === 0;

        return {
          id: config.id,
          label: config.label,
          count: count,
          dotColor: config.dotColor,
          backgroundColor: config.backgroundColor,
          borderColor: config.borderColor,
          textColor: config.textColor,
          hoverBackgroundColor: config.hoverBackgroundColor,
          hoverBorderColor: config.hoverBorderColor,
          hoverTextColor: config.hoverTextColor,
          disabled: isDisabled,
          disabledBackgroundColor: "#F9FAFB",
          disabledBorderColor: "#EAECF0",
          disabledTextColor: "#98A2B3",
          disabledDotColor: "#D0D5DD",
          themeCategoryKey: config.themeCategoryKey,
        };
      });


      return (
        <div className="account-summary-cell">
          {/* Reusable TagGroup Component */}
          <TagGroup
            tags={tagItems}
            showTotalTag={false}
            onTagClick={(tag) => {
              if (tag.themeCategoryKey) {
                onAccountSummaryTagClick?.(row.original, tag.themeCategoryKey as ThemeCategoryKey);
              }
            }}
          />

          {/* Paragraph Text */}
          <div className="account-summary-text">
            {summaryText}
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "timeline",
    header: "",
    Header: (
        <Box sx={{ pl: 4, pr: 4 }}>
        <TimelineHeader
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
        />
    </Box>
    ) as unknown as string,
    minSize: 1000,
    size: 1000,
    grow: true,
    enableColumnFilter: false,
    enableSorting: false,
    muiTableHeadCellProps: {
      sx: {
        paddingTop: "0px",
        borderBottom: "1px solid #7A7A8C",
      }
    },
    Cell: ({ row }) => {
    const filteredItems = (row.original.timelineInteractions || []).filter(
      (item: any) => {
        if (columnVisibility.interactions && !columnVisibility.email) {
          return item.type === "interaction";
        }

        if (columnVisibility.email && !columnVisibility.interactions) {
          return item.type === "email";
        }

        if (columnVisibility.email && columnVisibility.interactions) {
          return true;
        }

        return false;
      }
    );

    return (
      <Box
        sx={{
          whiteSpace: "nowrap",
          width: "100%",
          px: 5.5,
        }}
      >
        <Box sx={{ minWidth: "max-content" }}>
          <CustomerTimelineCell
            items={filteredItems}
            monthlyScore={row.original.monthlyScore}
            startDate={new Date(dateRange.startDate)}
            endDate={new Date(dateRange.endDate)}
            navigationState={{
              parent_page_identifier: row.original?.parentPageIdentifier,
              appliedFilters: row.original?.appliedFilters,
              customDateRange: row.original?.customDateRange,
              accountRelationshipType: columnFilters?.accountRelationshipType,
            }}
            tooltip={timelineTooltip}
            onTimelineClick={() => setOpenFilterColumn(null)}
          />
        </Box>
      </Box>
    );
  },
  }
]
};

export { getCustomerAccountColumns, initialSort };
